import type { EmailResult } from '@creanizer/types'
import { Mail, Paperclip, Reply, ReplyAll, Forward, Trash2, Archive } from 'lucide-react'

interface EmailViewerProps {
  document: EmailResult
}

export function EmailViewer({ document }: EmailViewerProps) {
  // データ検証
  if (!document || !document.subject || !document.body) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">メール</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  // 日時フォーマット
  const formatDateTime = (dateStr: string): string => {
    try {
      const date = new Date(dateStr)
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateStr
    }
  }
  
  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* メールヘッダー（Gmail風） */}
      <div className="bg-gray-50 border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            {document.subject}
          </h2>
          
          {/* アクションボタン */}
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
              <Reply className="w-4 h-4 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
              <ReplyAll className="w-4 h-4 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
              <Forward className="w-4 h-4 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
              <Archive className="w-4 h-4 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
              <Trash2 className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
        
        {/* 送受信者情報 */}
        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-gray-500 min-w-[60px]">差出人:</span>
            <div>
              <span className="font-medium text-gray-900">{document.from?.name || '送信者'}</span>
              <span className="text-gray-500 ml-2">&lt;{document.from?.address || 'unknown@example.com'}&gt;</span>
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <span className="text-gray-500 min-w-[60px]">宛先:</span>
            <div>
              <span className="font-medium text-gray-900">{document.to?.name || '受信者'}</span>
              <span className="text-gray-500 ml-2">&lt;{document.to?.address || 'unknown@example.com'}&gt;</span>
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <span className="text-gray-500 min-w-[60px]">日時:</span>
            <span className="text-gray-700">{formatDateTime(document.date)}</span>
          </div>
        </div>
        
        {/* 添付ファイル */}
        {document.attachments && document.attachments.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm">
              <Paperclip className="w-4 h-4 text-gray-500" />
              <span className="text-gray-500">添付ファイル:</span>
              <div className="flex flex-wrap gap-2">
                {document.attachments.map((file, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 cursor-pointer transition-colors"
                  >
                    {file}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* メール本文 */}
      <div className="p-6">
        <div className="prose prose-base max-w-none">
          {document.body.split('\n').map((paragraph, index) => (
            <p key={index} className="text-gray-700 mb-3 leading-relaxed">
              {paragraph || <br />}
            </p>
          ))}
        </div>
        
        {/* 署名区切り */}
        {document.body.includes('--') && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500 italic">
              このメールは架空の内容です
            </p>
          </div>
        )}
      </div>
      
      {/* フッター */}
      <div className="px-6 pb-4">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>文字数: {document.metadata.wordCount}</span>
          <span>生成日時: {document.metadata.generatedAt}</span>
        </div>
      </div>
    </div>
  )
}